import { supabase } from '../../../lib/supabase';
import { fetchAll } from '../../../lib/save';

/*
  THE JOB QUEUE, reading and writing, 4 October 2026.

  **A job is one arrival**, whichever of the five doors it came in by: a
  ClaimSoft transfer order, a client's purchase order, a client's email, a rep's
  email, or an order placed in OnHand. A 28 line transfer order is one job and a
  rep asking for one item is one job.

  **Nothing here processes anything.** What the owner does once he picks a job up
  is deliberately not designed yet, his own instruction, so that it does not get
  decided by accident while the queue is being built.
*/

export const DOORS = {
  transfer_order: 'ClaimSoft transfer order',
  client_pdf:     "Client's purchase order",
  client_email:   "Client's email",
  rep_email:      "Rep's email",
  placed_here:    'Placed in OnHand',
};

export const STATES = {
  needs_you:      { label: 'Needs you',      says: 'Standardised, but something could not be matched on its own.' },
  ready:          { label: 'Ready',          says: 'Standardised, every line matched, nothing waiting on anybody.' },
  working:        { label: 'Being worked',   says: 'Picked up and in progress.' },
  reading:        { label: 'Being read',     says: 'OnHand is still turning what arrived into the standard shape.' },
  could_not_read: { label: 'Could not read', says: 'What arrived could not be understood at all.' },
  done:           { label: 'Done',           says: 'Finished with.' },
  cancelled:      { label: 'Cancelled',      says: 'Withdrawn before it was worked.' },
};

/* Needs you first, because it is the only state where the queue is waiting on
   him rather than the other way round. */
export const QUEUE_ORDER = ['needs_you', 'ready', 'working', 'reading', 'could_not_read'];

export const URGENCY = {
  routine: { label: 'Routine', says: 'Goes on the next run for that place.' },
  soon:    { label: 'Soon',    says: 'Wanted sooner than the next run.' },
  urgent:  { label: 'Urgent',  says: 'Somebody is waiting on it.' },
};

/* What the Home card counts: everything he could pick up. Jobs OnHand is still
   reading, and jobs it could not read, are not his to start. */
export const PICKABLE = ['needs_you', 'ready', 'working'];

const JOB_COLS = `
  id, reference, door, state, destination_id, requested_by_name, source_reference,
  note, urgency, customer_po, batch_key, arrived_at, picked_up_at, picked_up_by,
  cancelled_at, cancelled_by, cancel_reason, processed_at, processed_by, finished_at,
  destination:warehouses!order_jobs_destination_id_fkey(id, name, onhand_name, region_name)
`;

/* `view` is 'open', everything still in the queue, or 'cancelled', the jobs he
   took out of it, kept so they can be seen and put back (5 October 2026). */
export async function loadQueue(view = 'open') {
  const { data, error } = await fetchAll(() => {
    const q = supabase.from('order_jobs').select(JOB_COLS);
    return (view === 'cancelled'
      ? q.eq('state', 'cancelled').order('cancelled_at', { ascending: false })
      /* Finished jobs are out of the queue and kept, like cancelled ones, so
         their ClaimSoft files can be downloaded again (stage 6). */
      : view === 'done'
      ? q.eq('state', 'done').order('finished_at', { ascending: false })
      : q.not('state', 'in', '(done,cancelled)').order('arrived_at', { ascending: false }));
  });
  if (error) return { jobs: [], lines: {}, error };

  const ids = (data || []).map(j => j.id);
  if (ids.length === 0) return { jobs: [], lines: {}, error: null };

  /* The line counts in one read rather than one per job. A queue of forty jobs
     should not be forty one requests. */
  const { data: lines, error: lineError } = await fetchAll(() => supabase
    .from('order_job_lines')
    .select('job_id, catalog_item_id, quantity')
    .in('job_id', ids));
  if (lineError) return { jobs: data || [], lines: {}, error: lineError };

  const by = {};
  for (const l of lines || []) {
    const t = by[l.job_id] || (by[l.job_id] = { lines: 0, matched: 0, units: 0 });
    t.lines += 1;
    if (l.catalog_item_id) t.matched += 1;
    t.units += Number(l.quantity) || 0;
  }
  return { jobs: data || [], lines: by, error: null };
}

export async function countWaiting() {
  const { count, error } = await supabase
    .from('order_jobs')
    .select('id', { count: 'exact', head: true })
    .in('state', PICKABLE);
  return { count: count || 0, error };
}

export function tally(jobs = [], lines = {}) {
  const out = { total: jobs.length, needs_you: 0, ready: 0, working: 0, reading: 0, could_not_read: 0, lines: 0 };
  for (const j of jobs) {
    if (out[j.state] !== undefined) out[j.state] += 1;
    out.lines += (lines[j.id] || {}).lines || 0;
  }
  return out;
}

/*
  **The reference a person says out loud.** Worked out here rather than by the
  database, because a sequence belongs to the whole database and this has to
  start at 1 for each company. It reads the highest already used and adds one,
  which is safe while one person places an order at a time and is the kind of
  thing to revisit the day that stops being true.
*/
export async function nextReference() {
  const { data, error } = await supabase
    .from('order_jobs')
    .select('reference')
    .order('reference', { ascending: false })
    .limit(1);
  if (error) return { reference: null, error };
  const last = data && data[0] ? parseInt(String(data[0].reference).replace(/\D/g, ''), 10) : 0;
  const n = (Number.isFinite(last) ? last : 0) + 1;
  return { reference: 'JOB-' + String(n).padStart(5, '0'), error: null };
}

/*
  **Placing an order writes the job, then its lines, then proves both.**

  A client order that could be saved twice if its lines failed is a recorded
  fault on the existing Client Ordering screen (audit part 3). This does the
  opposite: if the lines do not save, the job is removed again, so a half an
  order never sits in the queue looking whole.
*/
export async function placeOrder({ destinationId, lines, note, urgency, customerPo, requestedByName }) {
  if (!destinationId) return { ok: false, error: { message: 'Pick where this is going first.' } };
  const clean = (lines || []).filter(l => l.item_as_given && Number(l.quantity) > 0);
  if (clean.length === 0) return { ok: false, error: { message: 'Add at least one item.' } };

  const { reference, error: refError } = await nextReference();
  if (refError) return { ok: false, error: refError };

  const { data: job, error } = await supabase.from('order_jobs').insert({
    reference,
    door: 'placed_here',
    state: 'ready',
    destination_id: destinationId,
    requested_by_name: requestedByName || null,
    note: note || null,
    urgency: urgency || 'routine',
    customer_po: customerPo || null,
  }).select('id, reference').maybeSingle();

  if (error) return { ok: false, error };
  if (!job) return { ok: false, error: { message: 'That did not save. Nothing was changed.' } };

  const { error: lineError } = await supabase.from('order_job_lines').insert(
    clean.map((l, i) => ({
      job_id: job.id,
      line_no: i + 1,
      item_as_given: l.item_as_given,
      description: l.description || null,
      quantity: Number(l.quantity),
      catalog_item_id: l.catalog_item_id || null,
      vendor_id: l.vendor_id || null,
      note: l.note || null,
    })),
  );

  if (lineError) {
    await supabase.from('order_jobs').delete().eq('id', job.id);
    return { ok: false, error: lineError };
  }

  /* Read it back. A save that no rule allows changes no rows, raises nothing and
     answers "fine", which is this project's oldest and most expensive fault. */
  const { count } = await supabase
    .from('order_job_lines')
    .select('id', { count: 'exact', head: true })
    .eq('job_id', job.id);
  if ((count || 0) !== clean.length) {
    await supabase.from('order_jobs').delete().eq('id', job.id);
    return { ok: false, error: { message: 'Only ' + (count || 0) + ' of ' + clean.length + ' lines saved, so nothing was kept.' } };
  }

  return { ok: true, error: null, job: { ...job, lines: clean.length } };
}

/* The places an order can be sent to. Retired ones are left out: ClaimSoft has
   marked them dead and nothing should be ordered for them. */
export async function loadDestinations() {
  const { data, error } = await fetchAll(() => supabase
    .from('warehouses')
    .select('id, onhand_name, region_name, account_edge_customer')
    .not('onhand_name', 'is', null)
    .eq('is_inactive', false)
    .order('onhand_name'));
  return { places: data || [], error };
}

/*
  **Searching the catalog**, because there is nothing better to offer yet.

  He asked for a short list of what a place has ordered before. That wants the
  par levels out of the ClaimSoft stock status export, which OnHand does not
  hold, so this is a search across all 356,074 items instead. **Written down as a
  gap rather than papered over.**
*/
export async function searchCatalog(text) {
  const q = String(text || '').trim();
  if (q.length < 2) return { items: [], error: null };
  const safe = q.replace(/[%,()]/g, ' ');
  /*
    **It catches, because a search that throws leaves the screen saying
    "Looking..." for ever.** That is exactly what it did on the day it was
    written, and nothing on screen or in the console said why. A caller gets an
    error it can show; it never gets a promise that rejects.
  */
  try {
    const { data, error } = await supabase
      .from('catalog_items')
      .select('id, oh_number, vendor_part_number, our_description, vendor_description, vendor_id, vendor:vendors(name)')
      .or('vendor_part_number.ilike.%' + safe + '%,our_description.ilike.%' + safe + '%,vendor_description.ilike.%' + safe + '%')
      .limit(25);
    return { items: data || [], error };
  } catch (e) {
    return { items: [], error: { message: e && e.message ? e.message : String(e) } };
  }
}

/*
  ===========================================================================
  WORKING THE QUEUE, 5 October 2026
  ===========================================================================

  **Found by the owner the moment he opened the queue**: *"nothing lets me
  delete it, claim it or open it."* Opening a job, picking it up, putting it
  down and cancelling it are the queue rather than processing, so they are
  here. **What he does once a job is picked up is still not designed**, on his
  own instruction, and nothing below decides it.

  **His answers the same day**: a job he gets rid of is CANCELLED and kept, never
  deleted, and can be put back. Working two jobs together waits until he has
  said what the rules are.

  **Every write names the state it expects to find**, so two people cannot both
  pick up the same job, and a job somebody else has already moved is reported
  rather than overwritten. **And every write is read back**: an update that
  matches no row raises nothing and answers "fine", which is this project's
  oldest fault.
*/

/* The states a job can be picked up from. */
export const WAITING = ['needs_you', 'ready'];

/*
  **Ready means ready to be processed**, his rule of 11 October 2026, found on
  JOB-00003: *"the job queue now says this job is ready and it is not it has to
  have an account edge number before the job can be picked up for processing."*
  So a job is Ready only when it has a destination, every line is a known item,
  AND every line's item has an Account Edge number, because the Account Edge
  file cannot be built without one. Anything short of that is Needs you.

  A line says whether it has an Account Edge number in `ae_number`, which
  `withAccountEdge` below fills in. A line without the field counts as having
  none, so a caller that forgets to ask can only make a job wait, never let an
  unready one through.
*/
export function waitingState(job, lines = []) {
  if (!job || !job.destination_id) return 'needs_you';
  return lines.some(l => !l.catalog_item_id || !l.ae_number) ? 'needs_you' : 'ready';
}

/* Each line's item's main Account Edge number, as `ae_number` (null when it has
   none). One read for the whole job. */
export async function withAccountEdge(lines = []) {
  const ids = [...new Set(lines.map(l => l.catalog_item_id).filter(Boolean))];
  const ae = new Map();
  for (let i = 0; i < ids.length; i += 150) {
    const { data, error } = await supabase.from('org_items')
      .select('catalog_item_id, numbers:org_item_numbers(system, number, is_main)').in('catalog_item_id', ids.slice(i, i + 150));
    if (error) return { lines: lines.map(l => ({ ...l, ae_number: null })), error };
    for (const it of data || []) {
      const main = (it.numbers || []).find(n => n.system === 'account_edge' && n.is_main);
      if (main) ae.set(it.catalog_item_id, main.number);
    }
  }
  return { lines: lines.map(l => ({ ...l, ae_number: l.catalog_item_id ? ae.get(l.catalog_item_id) || null : null })), error: null };
}

/* Work a waiting job's state out again from its lines, and move it if it has
   changed. A job somebody has picked up is left alone. Returns the new state. */
export async function recheckJob(jobId) {
  const { data: job } = await supabase.from('order_jobs').select('id, reference, state, destination_id').eq('id', jobId).maybeSingle();
  if (!job || !WAITING.includes(job.state)) return job ? job.state : null;
  const { data: raw } = await fetchAll(() => supabase.from('order_job_lines').select('catalog_item_id').eq('job_id', jobId));
  const { lines } = await withAccountEdge(raw || []);
  const next = waitingState(job, lines);
  if (next === job.state) return next;
  const { data: moved } = await supabase.from('order_jobs').update({ state: next, updated_at: new Date().toISOString() })
    .eq('id', jobId).in('state', WAITING).select('state').maybeSingle();
  return moved ? moved.state : job.state;
}

/* Which buttons a job offers, by its state. Kept here so the rule can be tested
   without drawing the screen. */
/*
  **Only a Ready job can be picked up**, his answer of 11 October 2026: picking
  a job up is the start of processing it, and a job is not ready to process
  until everything on it is known. A Needs you job is fixed where it sits (each
  line has its own "Which item is this?" and "Which Account Edge item?") and
  turns Ready by itself.
*/
export function actionsFor(state) {
  if (state === 'ready') return ['pick_up', 'cancel'];
  if (state === 'needs_you') return ['cancel'];
  if (state === 'working') return ['put_down', 'cancel'];
  if (state === 'reading' || state === 'could_not_read') return ['cancel'];
  if (state === 'cancelled') return ['put_back'];
  return [];
}

const MOVED = 'This job changed while you were looking at it, so nothing was done. It has been read again.';

/* One job and every line on it, with the catalog's own view of each matched item. */
export async function loadJob(id) {
  const { data: job, error } = await supabase
    .from('order_jobs').select(JOB_COLS).eq('id', id).maybeSingle();
  if (error) return { job: null, lines: [], people: {}, error };
  if (!job) return { job: null, lines: [], people: {}, error: { message: 'That job could not be found.' } };

  const { data: lines, error: lineError } = await fetchAll(() => supabase
    .from('order_job_lines')
    .select('id, line_no, item_as_given, description, quantity, catalog_item_id, claimsoft_to_number, note, source_row, '
      + 'route, route_reason, final_item_number, final_catalog_item_id, final_quantity, audit, changed_by_hand, decided_at, '
      /* A line points at the catalog twice since stage 4 (the item asked for,
         and the item to use), so the join names which; without it the
         database refuses the whole request and no job opens. */
      + 'vendor:vendors(name), item:catalog_items!order_job_lines_catalog_item_id_fkey(vendor_part_number, our_description, vendor_description)')
    .eq('job_id', id)
    .order('line_no'));
  if (lineError) return { job, lines: [], people: {}, error: lineError };
  const withAe = await withAccountEdge(lines || []);

  /* The people the job names, by id. A name that cannot be read is left out and
     the screen says "someone" rather than failing the whole job. */
  const ids = [job.picked_up_by, job.cancelled_by].filter(Boolean);
  const people = {};
  if (ids.length) {
    const { data: who } = await supabase.from('profiles').select('id, full_name, email').in('id', ids);
    for (const p of who || []) people[p.id] = p.full_name || p.email;
  }
  return { job, lines: withAe.lines, people, error: null };
}

/* Shared shape for the four writes: update only if the job is still in a state
   the action expects, then prove a row changed. */
async function move(id, fromStates, patch) {
  const { data, error } = await supabase
    .from('order_jobs')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id)
    .in('state', fromStates)
    .select('id, state')
    .maybeSingle();
  if (error) return { ok: false, error };
  if (!data || data.state !== patch.state) return { ok: false, error: { message: MOVED } };
  return { ok: true, error: null };
}

export function pickUp(id, userId) {
  /* Ready only, here as well as on the screen, so a job that slipped back to
     Needs you a moment ago cannot be picked up from a stale page. */
  return move(id, ['ready'], {
    state: 'working', picked_up_by: userId || null, picked_up_at: new Date().toISOString(),
  });
}

export function putDown(job, lines) {
  return move(job.id, ['working'], {
    state: waitingState(job, lines), picked_up_by: null, picked_up_at: null,
  });
}

export function cancelJob(id, userId, reason) {
  return move(id, ['needs_you', 'ready', 'working', 'reading', 'could_not_read'], {
    state: 'cancelled',
    cancelled_at: new Date().toISOString(),
    cancelled_by: userId || null,
    cancel_reason: String(reason || '').trim() || null,
    picked_up_by: null, picked_up_at: null,
  });
}

export function putBack(job, lines) {
  return move(job.id, ['cancelled'], {
    state: waitingState(job, lines), cancelled_at: null, cancelled_by: null, cancel_reason: null,
  });
}

/* **Finished**, ordering stage 6, 12 October 2026: only a job being worked can
   be finished, and a finished job leaves the queue, his word of 11 October:
   *"if a job has been sent for processing and processing has been completed it
   no longer needs to stay in this job queue."* */
export function finishJob(id) {
  return move(id, ['working'], { state: 'done', finished_at: new Date().toISOString() });
}
